import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { themeVars } from '@mds/ui';
import React, { useEffect, FC, useCallback, useMemo, useState } from 'react';

import { isInViewport } from '../../utils/element-position.utils';
import { LogAction, LogLevel } from '../providers/logger/logger-types';
import { useLogger } from '../providers/logger/Logger';
import { useUser } from '../providers/UserProvider';
import Player from '@vimeo/player';
import PreventCopyPaste from './PreventCopyPaste';
import Watermark from './Watermark';
import { setBuzzsproutPlayerHeight } from '../../utils/utils';
import { isChartsArticle, makeCharts } from '../../utils/tableau';
import { useApolloClient } from '@apollo/react-hooks';
import { useHistory } from 'react-router-dom';
import mediumZoom from 'medium-zoom';
import type { SxProps, Theme as ExtendedTheme } from '@mui/material/styles';
import { normalizeImageSourcesInHtml } from '../../utils/image-url.utils';
import {
    getPrintDividerColor,
    getPrintTextPrimaryColor,
} from '../../utils/print-theme.utils';
interface ArticleProps {
    children?: React.ReactNode;
    id: string;
    title: string;
    html: string;
    showWatermark?: boolean;
    watermarkText?: string;
    isMarketing?: boolean;
}

const articleContentSx: SxProps<ExtendedTheme> = (theme: ExtendedTheme) => ({
    '& a': {
        color: themeVars(theme).palette.link.default,
        textDecoration: 'none',
        '&:hover': {
            textDecoration: 'none',
            color: themeVars(theme).palette.link.default,
        },
        '&:focus': {
            textDecoration: 'none',
        },
        '& > u': {
            textDecoration: 'none',
        },
    },
    '& > :first-of-type': {
        marginTop: '0 !important',
    },
    '& h1': {
        margin: theme.spacing(3, 0, 1, 0),
        ...theme.typography.h4,
        [theme.breakpoints.up('xs')]: {
            ...theme.typography.h5,
        },
        [theme.breakpoints.up('md')]: {
            ...theme.typography.h4,
        },
        [theme.breakpoints.up('xl')]: {
            ...theme.typography.h3,
        },
    },
    '& h2': {
        margin: theme.spacing(2, 0, 1, 0),
        ...theme.typography.h5,
        [theme.breakpoints.up('xs')]: {
            ...theme.typography.h6,
        },
        [theme.breakpoints.up('md')]: {
            ...theme.typography.h5,
        },
        [theme.breakpoints.up('xl')]: {
            ...theme.typography.h4,
        },
    },
    '& h3': {
        margin: theme.spacing(2, 0, 1, 0),
        ...theme.typography.h6,
        [theme.breakpoints.up('xs')]: {
            ...theme.typography.subtitle1,
        },
        [theme.breakpoints.up('md')]: {
            ...theme.typography.h6,
        },
        [theme.breakpoints.up('xl')]: {
            ...theme.typography.h5,
        },
    },
    '& p, & li, & ol, & p + p': {
        [theme.breakpoints.up('xl')]: {
            ...theme.typography.body1,
        },
        margin: theme.spacing(1, 0),
        ...theme.typography.body1,
    },
    '& table': {
        color: `${themeVars(theme).palette.text.primary} !important`,
        borderColor: `${themeVars(theme).palette.divider} !important`,
        display: 'block',
        maxWidth: '100%',
        maxHeight: '50vh',
        overflowX: 'auto',
        overflowY: 'auto',
    },
    '& table.fr-table-align-left': {
        maxWidth: '100%',
        marginLeft: 0,
        marginRight: 'auto',
    },
    '& table.fr-table-align-center': {
        maxWidth: '100%',
        marginLeft: 'auto',
        marginRight: 'auto',
    },
    '& table.fr-table-align-right': {
        maxWidth: '100%',
        marginLeft: 'auto',
        marginRight: 0,
    },
    '& table th, & table td, & table th *, & table td *': {
        color: `${themeVars(theme).palette.text.primary} !important`,
        borderColor: `${themeVars(theme).palette.divider} !important`,
    },
    '& span.fr-img-caption.fr-dii': {
        [theme.breakpoints.down('md')]: {
            display: 'flex',
            width: 'auto !important',
        },
    },
    '& span.fr-img-caption.fr-dib': {
        [theme.breakpoints.down('md')]: {
            display: 'flex',
            width: 'auto !important',
        },
    },
    '& .fr-img-caption': {
        width: '100%',
        [theme.breakpoints.down('md')]: {
            margin: '0 auto !important',
        },
        '& img': {
            width: 'auto !important',
        },
    },
    '& .fr-img-space-wrap': {
        ...theme.typography.body1,
        [theme.breakpoints.down('md')]: {
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
            fontSize: theme.typography.body2.fontSize,
            lineHeight: theme.typography.body1.lineHeight,
        },
    },
    '& img': {
        borderRadius: theme.shape.borderRadius,
        '@media print': {
            pageBreakInside: 'avoid',
        },
    },
    '& .fr-img-caption .fr-img-wrap > span': theme.typography.caption,
    [theme.breakpoints.down('md')]: {
        marginBottom: theme.spacing(4),
    },
    '& .promotional-content > img': {
        display: 'block',
        width: '100%',
        height: 'auto',
        borderRadius: theme.shape.borderRadius,
        transition: 'opacity .3s ease-out',
    },
    '& .promotional-content:hover > img': {
        opacity: 0.8,
    },
    [theme.breakpoints.down('xs')]: {
        '& .promotional-content': {
            display: 'none',
        },
    },
    '& > .download-xls-spectrometer-report-block': {
        display: 'flex',
        justifyContent: 'space-between',
        background: themeVars(theme).palette.background.paper,
        padding: theme.spacing(1, 2),
        borderRadius: theme.shape.borderRadius,
        '& > div:first-of-type': {
            display: 'flex',
            flexDirection: 'column',
            '& > h2': {
                fontSize: 16,
                color: themeVars(theme).palette.text.primary,
                marginBottom: 0,
                marginTop: theme.spacing(1),
                [theme.breakpoints.down('sm')]: {
                    fontSize: 14,
                    lineHeight: theme.typography.body1.lineHeight,
                },
            },
            '& > p': {
                fontSize: 12,
                marginTop: 0,
                color: themeVars(theme).palette.text.secondary,
                lineHeight: theme.typography.body1.lineHeight,
                [theme.breakpoints.down('sm')]: {
                    fontSize: 11,
                    lineHeight: theme.typography.body1.lineHeight,
                },
            },
        },
        '& > div:last-child': {
            display: 'flex',
            alignItems: 'center',
            '& > button': {
                cursor: 'pointer',
                outline: 'none',
                border: 'none',
                boxShadow: 'none',
                fontSize: 12,
                lineHeight: theme.typography.body1.lineHeight,
                fontWeight: 600,
                background: themeVars(theme).palette.primary.main,
                color: themeVars(theme).palette.primary.contrastText,
                padding: theme.spacing(1, 2),
                borderRadius: theme.shape.borderRadius,
                '&:hover, &:active': {
                    background: themeVars(theme).palette.primary.dark,
                },
                [theme.breakpoints.down('xs')]: {
                    width: '100%',
                    marginTop: theme.spacing(1),
                },
            },
        },
        [theme.breakpoints.down('xs')]: {
            flexDirection: 'column',
            padding: theme.spacing(2),
        },
    },
    '@media print': {
        color: getPrintTextPrimaryColor(theme),
        '& table': {
            display: 'table',
            maxHeight: 'none',
            overflow: 'visible',
        },
        '& table, & table th, & table td, & table th *, & table td *': {
            color: `${getPrintTextPrimaryColor(theme)} !important`,
            borderColor: `${getPrintDividerColor(theme)} !important`,
        },
    },
});

const articleContentChartsSx: SxProps<ExtendedTheme> = (
    theme: ExtendedTheme
) => ({
    [theme.breakpoints.up('md')]: {
        '& > :not([data-id=tableau-viz])': {
            maxWidth: 720,
        },
    },
    [theme.breakpoints.up('lg')]: {
        '& > :not([data-id=tableau-viz])': {
            maxWidth: 920,
        },
    },
});

const ArticleContent: FC<ArticleProps> = ({
    html,
    title,
    id,
    children,
    showWatermark,
    isMarketing,
    watermarkText,
}) => {
    const theme = useTheme();
    const zoomBackground = themeVars(theme).palette.background.default;
    const history = useHistory();
    const [mountedTimestamp, setMountedTimestamp] = useState(0);
    const [loggedPromotionalContentIds, setLoggedPromotionalContentIds] =
        useState(new Set());
    const [loggedVideoUrls, setLoggedVideoUrls] = useState(new Set());
    const { printLog } = useLogger();
    const { userDetails } = useUser();
    const client = useApolloClient();
    const normalizedHtml = useMemo(
        () => normalizeImageSourcesInHtml(html ?? ''),
        [html]
    );

    const isInViewportHandler = useCallback(() => {
        document
            .querySelectorAll<HTMLLinkElement>('.promotional-content')
            .forEach((element) => {
                if (isInViewport<HTMLLinkElement>(element)) {
                    const promotionalContentName = element.getAttribute(
                        'data-promotional-name'
                    );
                    const promotionalContentId = element.getAttribute(
                        'data-promotional-id'
                    );

                    if (
                        !loggedPromotionalContentIds.has(promotionalContentId)
                    ) {
                        printLog(
                            LogLevel.INFO,
                            LogAction.BodyImpression,
                            'Promotional Content',
                            {
                                name: promotionalContentName,
                                id: promotionalContentId,
                                articleName: title,
                                articleId: id,
                                userId: userDetails?.id,
                                whenMs: Date.now() - mountedTimestamp,
                            }
                        );

                        setLoggedPromotionalContentIds((prev) =>
                            prev.add(promotionalContentId)
                        );
                    }
                }
            });
    }, [
        loggedPromotionalContentIds,
        printLog,
        title,
        id,
        userDetails,
        mountedTimestamp,
    ]);

    const clickHandler = useCallback(
        (e: MouseEvent) => {
            const currentTarget = e.currentTarget as HTMLAnchorElement;
            const promotionalContentName = currentTarget.getAttribute(
                'data-promotional-name'
            );
            const promotionalContentId = currentTarget.getAttribute(
                'data-promotional-id'
            );
            const promotionalContentLink = currentTarget.getAttribute('href');
            const promotionalContentTarget =
                currentTarget.getAttribute('target');

            printLog(
                LogLevel.INFO,
                LogAction.BodyClick,
                'Promotional Content',
                {
                    name: promotionalContentName,
                    id: promotionalContentId,
                    articleName: title,
                    articleId: id,
                    userId: userDetails?.id,
                    whenMs: Date.now() - mountedTimestamp,
                }
            );

            if (!promotionalContentTarget) {
                e.preventDefault();
                if (promotionalContentLink) {
                    history.push(promotionalContentLink);
                }
            }
        },
        [history, id, mountedTimestamp, printLog, title, userDetails]
    );

    const videoPlayHandler = useCallback(
        (videoWrapper: Element) => {
            const iframe: HTMLIFrameElement | null =
                videoWrapper.querySelector('iframe');

            if (iframe && iframe?.src.includes('vimeo')) {
                const videoUrl = iframe.src;
                const player = new Player(iframe);

                player.on('play', async () => {
                    if (!loggedVideoUrls.has(videoUrl)) {
                        const videoName = await player.getVideoTitle();

                        printLog(
                            LogLevel.INFO,
                            LogAction.VideoStarted,
                            'Embedded Video',
                            {
                                videoName,
                                videoUrl,
                                articleName: title,
                                articleId: id,
                                whenMs: Date.now() - mountedTimestamp,
                            }
                        );

                        setLoggedVideoUrls((prev) => prev.add(videoUrl));
                    }
                });
            }
        },
        [id, loggedVideoUrls, mountedTimestamp, printLog, title]
    );

    useEffect(() => {
        isInViewportHandler();
        window.addEventListener('scroll', isInViewportHandler);
        return () =>
            window.removeEventListener('scroll', isInViewportHandler, false);
    }, [isInViewportHandler]);

    useEffect(() => {
        const promotionalContentLinks =
            document.querySelectorAll<HTMLLinkElement>('.promotional-content');

        promotionalContentLinks.forEach((element) => {
            element.addEventListener('click', clickHandler);
        });

        return () =>
            promotionalContentLinks.forEach((element) => {
                element.removeEventListener('click', clickHandler, false);
            });
    }, [clickHandler, normalizedHtml]);

    useEffect(() => {
        document.querySelectorAll('.fr-video').forEach(videoPlayHandler);
    }, [videoPlayHandler, normalizedHtml]);

    useEffect(() => {
        setMountedTimestamp(Date.now());
    }, []);

    useEffect(() => {
        setMountedTimestamp(Date.now());
    }, []);

    useEffect(() => {
        setBuzzsproutPlayerHeight();
        const images = document
            .getElementById('article-content')
            ?.querySelectorAll('img:not(.promotional-content-image)');

        if (images) {
            const imagesArray = [...images] as [HTMLImageElement];

            imagesArray.forEach((image) => {
                mediumZoom(image, {
                    background: zoomBackground,
                });
            });
        }
    }, [normalizedHtml, zoomBackground]);

    useEffect(() => {
        const downloadXlsButton = document.getElementById(
            'download-xls-spectrometer-report-button'
        );
        const downloadReportLink =
            downloadXlsButton?.getAttribute('data-xls-link');

        if (downloadXlsButton && downloadReportLink) {
            const downloadReportButtonClickHandler = async () => {
                window.open(`https:${downloadReportLink}`, '_blank');

                printLog(LogLevel.INFO, LogAction.DownloadData, 'Excel', {
                    fileLink: downloadReportLink,
                    articleName: title,
                    articleId: id,
                    userId: userDetails?.id,
                });
            };

            downloadXlsButton.addEventListener(
                'click',
                downloadReportButtonClickHandler
            );

            return downloadXlsButton.addEventListener(
                'click',
                downloadReportButtonClickHandler,
                false
            );
        }
    }, [normalizedHtml, id, mountedTimestamp, printLog, title, userDetails]);

    const chartsArticle = isChartsArticle(normalizedHtml);

    const articleContent = (
        <Box
            component="div"
            id="article-content"
            className="fr-view"
            sx={[
                articleContentSx,
                ...(chartsArticle ? [articleContentChartsSx] : []),
            ]}
            ref={(content: HTMLDivElement | null) =>
                content && makeCharts(client, content)
            }
            dangerouslySetInnerHTML={{ __html: normalizedHtml }}
        >
            {children}
        </Box>
    );

    const content = showWatermark ? (
        <Watermark watermarkText={watermarkText}>{articleContent}</Watermark>
    ) : (
        articleContent
    );

    return isMarketing ? (
        content
    ) : (
        <PreventCopyPaste>{content}</PreventCopyPaste>
    );
};

export default ArticleContent;
